'use client';

import React from 'react';

interface CarIllustrationProps {
  type: string;
  className?: string;
}

export function CarIllustration({ type, className = 'w-full h-auto' }: CarIllustrationProps) {
  switch (type) {
    case 'toyota_corolla':
      // White Compact Sedan
      return (
        <svg
          viewBox="0 0 500 240"
          className={className}
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Ground reflection & shadow */}
          <ellipse cx="250" cy="216" rx="210" ry="14" fill="#000000" fillOpacity="0.12" filter="blur(6px)" />
          <ellipse cx="250" cy="218" rx="170" ry="8" fill="#000000" fillOpacity="0.2" filter="blur(3px)" />

          {/* Car Body - Main Shell (Pure White / Crisp Silver highlights) */}
          <path
            d="M50 170 C55 140, 75 130, 110 125 L160 115 L220 75 C245 60, 310 60, 350 78 L415 120 C445 125, 465 145, 465 172 C465 190, 445 195, 430 195 L395 195 C390 165, 355 165, 350 195 L170 195 C165 165, 130 165, 125 195 L70 195 C55 195, 50 185, 50 170 Z"
            fill="url(#corolla-body-gradient)"
          />

          {/* Cabin & Roof line */}
          <path
            d="M170 115 L225 76 C245 63, 305 63, 345 80 L405 120 Z"
            fill="url(#corolla-roof-gradient)"
          />

          {/* Windows / Windshield */}
          <path
            d="M178 112 L228 80 C242 70, 290 70, 292 70 L292 112 Z"
            fill="url(#glass-gradient)"
          />
          <path
            d="M300 70 C305 70, 340 76, 348 83 L398 115 L300 115 Z"
            fill="url(#glass-gradient)"
          />
          {/* Window B-pillar */}
          <rect x="292" y="70" width="8" height="43" fill="#1e293b" />

          {/* Character lines & Door seams */}
          <path d="M120 135 Q 260 130, 425 132" stroke="#cbd5e1" strokeWidth="2.5" />
          <path d="M210 115 L200 185" stroke="#cbd5e1" strokeWidth="1.5" />
          <path d="M310 115 L310 185" stroke="#cbd5e1" strokeWidth="1.5" />
          {/* Door handles */}
          <rect x="220" y="132" width="18" height="4" rx="2" fill="#94a3b8" />
          <rect x="325" y="132" width="18" height="4" rx="2" fill="#94a3b8" />

          {/* Front Bumper & Modern Grille */}
          <path
            d="M420 138 C440 142, 458 152, 462 168 L420 188 Z"
            fill="#e2e8f0"
          />
          <path
            d="M430 155 C442 155, 452 162, 454 172 C445 180, 430 182, 420 182 Z"
            fill="#1e293b"
          />
          {/* Grille mesh horizontal bars */}
          <line x1="428" y1="162" x2="450" y2="162" stroke="#475569" strokeWidth="1.5" />
          <line x1="425" y1="168" x2="448" y2="168" stroke="#475569" strokeWidth="1.5" />
          <line x1="422" y1="174" x2="442" y2="174" stroke="#475569" strokeWidth="1.5" />

          {/* Headlights (Crisp LED Daytime running light) */}
          <path
            d="M410 128 L448 138 C450 144, 442 148, 428 146 L405 135 Z"
            fill="url(#headlight-gradient)"
          />
          <path d="M412 131 L446 140" stroke="#38bdf8" strokeWidth="2" />

          {/* Rear Taillight */}
          <path
            d="M52 150 C55 142, 68 140, 80 140 L76 155 C65 156, 54 156, 52 150 Z"
            fill="#ef4444"
          />

          {/* Front Wheel Arch & Wheel */}
          <circle cx="372" cy="192" r="34" fill="#0f172a" />
          <circle cx="372" cy="192" r="28" fill="#1e293b" />
          <circle cx="372" cy="192" r="23" fill="#cbd5e1" />
          <circle cx="372" cy="192" r="8" fill="#0f172a" />
          {/* Rim spokes */}
          <path d="M372 169 L372 215 M349 192 L395 192 M356 176 L388 208 M356 208 L388 176" stroke="#475569" strokeWidth="3.5" />
          <circle cx="372" cy="192" r="5" fill="#f8fafc" />

          {/* Rear Wheel Arch & Wheel */}
          <circle cx="148" cy="192" r="34" fill="#0f172a" />
          <circle cx="148" cy="192" r="28" fill="#1e293b" />
          <circle cx="148" cy="192" r="23" fill="#cbd5e1" />
          <circle cx="148" cy="192" r="8" fill="#0f172a" />
          {/* Rim spokes */}
          <path d="M148 169 L148 215 M125 192 L171 192 M132 176 L164 208 M132 208 L164 176" stroke="#475569" strokeWidth="3.5" />
          <circle cx="148" cy="192" r="5" fill="#f8fafc" />

          {/* Side Mirror */}
          <path d="M380 112 C390 110, 396 115, 394 122 L378 122 Z" fill="#f8fafc" stroke="#cbd5e1" strokeWidth="1" />

          {/* Gradients */}
          <defs>
            <linearGradient id="corolla-body-gradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#ffffff" />
              <stop offset="60%" stopColor="#f1f5f9" />
              <stop offset="100%" stopColor="#cbd5e1" />
            </linearGradient>
            <linearGradient id="corolla-roof-gradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#ffffff" />
              <stop offset="100%" stopColor="#e2e8f0" />
            </linearGradient>
            <linearGradient id="glass-gradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#334155" />
              <stop offset="100%" stopColor="#0f172a" />
            </linearGradient>
            <linearGradient id="headlight-gradient" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#e2e8f0" />
              <stop offset="70%" stopColor="#ffffff" />
              <stop offset="100%" stopColor="#bae6fd" />
            </linearGradient>
          </defs>
        </svg>
      );

    case 'hyundai_tucson':
      // Dark Metallic SUV
      return (
        <svg
          viewBox="0 0 500 240"
          className={className}
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Ground reflection & shadow */}
          <ellipse cx="250" cy="218" rx="215" ry="14" fill="#000000" fillOpacity="0.16" filter="blur(6px)" />
          <ellipse cx="250" cy="220" rx="175" ry="8" fill="#000000" fillOpacity="0.25" filter="blur(3px)" />

          {/* SUV Roof Rails */}
          <path d="M190 52 L360 54" stroke="#475569" strokeWidth="4" strokeLinecap="round" />
          <line x1="210" y1="53" x2="210" y2="58" stroke="#334155" strokeWidth="3" />
          <line x1="340" y1="54" x2="340" y2="60" stroke="#334155" strokeWidth="3" />

          {/* SUV Body Shell (Dark Metallic Charcoal/Shadow Slate) */}
          <path
            d="M45 165 C50 130, 70 120, 105 115 L165 105 L215 56 C235 52, 335 52, 375 62 L430 110 C460 118, 475 140, 472 170 C470 192, 450 198, 430 198 L398 198 C392 162, 352 162, 346 198 L172 198 C166 162, 126 162, 120 198 L65 198 C50 198, 45 185, 45 165 Z"
            fill="url(#tucson-body-gradient)"
          />

          {/* High SUV Windows */}
          <path
            d="M172 102 L220 62 C235 56, 290 56, 292 56 L292 102 Z"
            fill="url(#suv-glass-gradient)"
          />
          <path
            d="M300 56 C305 56, 350 62, 368 68 L418 105 L300 105 Z"
            fill="url(#suv-glass-gradient)"
          />
          <rect x="292" y="56" width="8" height="48" fill="#090d14" />

          {/* Geometric Facets / Character Creases (Tucson signature style) */}
          <path d="M110 125 L240 122 L370 118 L440 126" stroke="#475569" strokeWidth="2" />
          <path d="M180 130 L260 170 L340 130" stroke="#334155" strokeWidth="1.5" />
          {/* Door seams & cladding */}
          <path d="M210 105 L200 188" stroke="#334155" strokeWidth="1.5" />
          <path d="M310 105 L310 188" stroke="#334155" strokeWidth="1.5" />
          {/* Lower plastic body protection cladding */}
          <path d="M60 188 L122 188 C126 166, 166 166, 170 188 L348 188 C352 166, 392 166, 396 188 L435 188 C455 188, 465 182, 465 174 L420 188 Z" fill="#0f172a" />

          {/* Door Handles */}
          <rect x="225" y="125" width="20" height="5" rx="2" fill="#64748b" />
          <rect x="330" y="125" width="20" height="5" rx="2" fill="#64748b" />

          {/* Tucson Parametric Jewel Grille */}
          <path
            d="M435 125 C455 130, 468 144, 470 162 L425 182 Z"
            fill="#090d14"
          />
          {/* Parametric geometric lights */}
          <polygon points="440,132 448,134 445,142 437,140" fill="#cbd5e1" />
          <polygon points="450,135 458,138 455,146 447,144" fill="#f8fafc" />
          <polygon points="458,140 464,144 461,152 455,149" fill="#38bdf8" />
          <polygon points="445,146 454,149 450,158 442,155" fill="#94a3b8" />
          <polygon points="436,155 446,158 442,168 433,165" fill="#64748b" />

          {/* Taillight fang accent */}
          <path d="M50 142 L72 138 L68 155 Z" fill="#ef4444" />

          {/* Rugged SUV Wheels (Dark Diamond-Cut Alloy) */}
          <circle cx="372" cy="192" r="36" fill="#020617" />
          <circle cx="372" cy="192" r="30" fill="#0f172a" />
          <circle cx="372" cy="192" r="24" fill="#334155" />
          <circle cx="372" cy="192" r="9" fill="#090d14" />
          <path d="M372 168 L372 216 M348 192 L396 192 M355 175 L389 209 M355 209 L389 175" stroke="#94a3b8" strokeWidth="4" />
          <circle cx="372" cy="192" r="5" fill="#e2e8f0" />

          <circle cx="144" cy="192" r="36" fill="#020617" />
          <circle cx="144" cy="192" r="30" fill="#0f172a" />
          <circle cx="144" cy="192" r="24" fill="#334155" />
          <circle cx="144" cy="192" r="9" fill="#090d14" />
          <path d="M144 168 L144 216 M120 192 L168 192 M127 175 L161 209 M127 209 L161 175" stroke="#94a3b8" strokeWidth="4" />
          <circle cx="144" cy="192" r="5" fill="#e2e8f0" />

          {/* Gradients */}
          <defs>
            <linearGradient id="tucson-body-gradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#334155" />
              <stop offset="50%" stopColor="#1e293b" />
              <stop offset="100%" stopColor="#0f172a" />
            </linearGradient>
            <linearGradient id="suv-glass-gradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#1e293b" />
              <stop offset="100%" stopColor="#020617" />
            </linearGradient>
          </defs>
        </svg>
      );

    case 'bmw_5series':
      // Deep Black Luxury Executive Sedan
      return (
        <svg
          viewBox="0 0 500 240"
          className={className}
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Ground reflection & shadow */}
          <ellipse cx="250" cy="216" rx="215" ry="13" fill="#000000" fillOpacity="0.22" filter="blur(6px)" />
          <ellipse cx="250" cy="218" rx="175" ry="7" fill="#000000" fillOpacity="0.35" filter="blur(3px)" />

          {/* Sleek Low Profile Executive Body (Metallic Gloss Black) */}
          <path
            d="M46 172 C50 140, 72 130, 108 124 L162 112 L224 70 C248 55, 318 56, 360 74 L422 116 C455 120, 474 142, 470 172 C468 192, 448 196, 428 196 L396 196 C390 162, 352 162, 346 196 L168 196 C162 162, 124 162, 118 196 L64 196 C48 196, 45 186, 46 172 Z"
            fill="url(#bmw-body-gradient)"
          />

          {/* Roof line & Hofmeister Kink (BMW Signature rear window curve) */}
          <path
            d="M174 110 L228 72 C248 59, 312 59, 355 76 L414 115 Z"
            fill="url(#bmw-roof-gradient)"
          />

          {/* Tinted Executive Windows */}
          <path
            d="M182 108 L232 74 C246 64, 296 64, 298 64 L298 108 Z"
            fill="url(#bmw-glass-gradient)"
          />
          <path
            d="M306 64 C310 64, 348 70, 356 78 L406 110 L306 110 Z"
            fill="url(#bmw-glass-gradient)"
          />
          {/* Chrome Trim around windows */}
          <path d="M178 110 L228 73 C248 58, 315 58, 356 75 L412 112" stroke="#94a3b8" strokeWidth="1.5" />
          <rect x="298" y="64" width="8" height="45" fill="#090d14" />

          {/* Sculpted Hood & Shoulder Lines */}
          <path d="M115 132 Q 260 126, 435 128" stroke="#334155" strokeWidth="2" />
          <path d="M210 110 L200 186" stroke="#1e293b" strokeWidth="1.5" />
          <path d="M315 110 L315 186" stroke="#1e293b" strokeWidth="1.5" />

          {/* Chrome Door Handles with illumination */}
          <rect x="225" y="128" width="18" height="4" rx="2" fill="#cbd5e1" />
          <rect x="330" y="128" width="18" height="4" rx="2" fill="#cbd5e1" />

          {/* Iconic BMW Kidney Grille with Chrome surround */}
          <path
            d="M438 140 C452 143, 464 150, 466 164 L426 182 Z"
            fill="#05080f"
          />
          {/* Left Kidney */}
          <ellipse cx="438" cy="154" rx="9" ry="12" fill="#090d14" stroke="#94a3b8" strokeWidth="1.5" />
          <line x1="438" y1="144" x2="438" y2="164" stroke="#475569" strokeWidth="1.5" />
          {/* Right Kidney */}
          <ellipse cx="454" cy="156" rx="9" ry="12" fill="#090d14" stroke="#94a3b8" strokeWidth="1.5" />
          <line x1="454" y1="146" x2="454" y2="166" stroke="#475569" strokeWidth="1.5" />

          {/* BMW Icon Laser LED Headlights (Twin C-Rings with Blue Laser accent) */}
          <path
            d="M414 124 L458 135 C462 142, 452 148, 436 145 L408 132 Z"
            fill="url(#bmw-light-gradient)"
          />
          <path d="M418 128 L448 135" stroke="#38bdf8" strokeWidth="2.5" />
          <circle cx="430" cy="132" r="3" fill="#bae6fd" />
          <circle cx="444" cy="136" r="3" fill="#bae6fd" />

          {/* BMW Roundel on Hood */}
          <circle cx="426" cy="132" r="4" fill="#0284c7" stroke="#cbd5e1" strokeWidth="1" />

          {/* Rear L-Shaped OLED Taillight */}
          <path
            d="M48 152 C52 144, 66 142, 78 142 L74 155 C64 156, 52 156, 48 152 Z"
            fill="#dc2626"
          />
          <path d="M52 148 L76 146" stroke="#f87171" strokeWidth="2" />

          {/* M Sport Multi-spoke Black & Silver Wheels */}
          <circle cx="372" cy="192" r="35" fill="#000000" />
          <circle cx="372" cy="192" r="29" fill="#0f172a" />
          <circle cx="372" cy="192" r="24" fill="#334155" />
          <circle cx="372" cy="192" r="8" fill="#020617" />
          {/* Dual tone spokes */}
          <path d="M372 168 L372 216 M348 192 L396 192 M355 175 L389 209 M355 209 L389 175" stroke="#e2e8f0" strokeWidth="3" />
          <path d="M362 170 L382 214 M350 202 L394 182" stroke="#64748b" strokeWidth="2" />
          <circle cx="372" cy="192" r="5" fill="#38bdf8" />

          <circle cx="144" cy="192" r="35" fill="#000000" />
          <circle cx="144" cy="192" r="29" fill="#0f172a" />
          <circle cx="144" cy="192" r="24" fill="#334155" />
          <circle cx="144" cy="192" r="8" fill="#020617" />
          <path d="M144 168 L144 216 M120 192 L168 192 M127 175 L161 209 M127 209 L161 175" stroke="#e2e8f0" strokeWidth="3" />
          <path d="M134 170 L154 214 M122 202 L166 182" stroke="#64748b" strokeWidth="2" />
          <circle cx="144" cy="192" r="5" fill="#38bdf8" />

          {/* Gradients */}
          <defs>
            <linearGradient id="bmw-body-gradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#1e293b" />
              <stop offset="40%" stopColor="#0f172a" />
              <stop offset="100%" stopColor="#020617" />
            </linearGradient>
            <linearGradient id="bmw-roof-gradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#334155" />
              <stop offset="100%" stopColor="#0f172a" />
            </linearGradient>
            <linearGradient id="bmw-glass-gradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#1e293b" />
              <stop offset="100%" stopColor="#05080f" />
            </linearGradient>
            <linearGradient id="bmw-light-gradient" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#334155" />
              <stop offset="60%" stopColor="#e0f2fe" />
              <stop offset="100%" stopColor="#38bdf8" />
            </linearGradient>
          </defs>
        </svg>
      );

    case 'ford_mustang':
      // White Convertible Muscle Car (Top Down)
      return (
        <svg
          viewBox="0 0 500 240"
          className={className}
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Ground reflection & shadow */}
          <ellipse cx="250" cy="216" rx="215" ry="14" fill="#000000" fillOpacity="0.14" filter="blur(6px)" />
          <ellipse cx="250" cy="218" rx="175" ry="8" fill="#000000" fillOpacity="0.22" filter="blur(3px)" />

          {/* Interior Headrests & Steering Wheel (Top down visible!) */}
          <path d="M228 102 C228 92, 240 92, 240 102 Z" fill="#1e293b" />
          <path d="M280 102 C280 92, 292 92, 292 102 Z" fill="#1e293b" />
          <path d="M336 104 C336 94, 344 94, 344 104 Z" fill="#0f172a" />
          <circle cx="340" cy="110" r="8" stroke="#334155" strokeWidth="2.5" fill="none" />

          {/* Raked Windshield (Frameless convertible windshield) */}
          <path
            d="M312 90 L368 116 L298 116 Z"
            fill="url(#mustang-glass-gradient)"
          />
          <line x1="312" y1="90" x2="368" y2="116" stroke="#cbd5e1" strokeWidth="2.5" />

          {/* Mustang Muscular Body (Oxford White with aggressive haunches) */}
          <path
            d="M48 168 C52 138, 76 128, 120 125 L190 120 L275 116 L375 116 L435 125 C465 130, 476 148, 472 172 C468 192, 450 196, 428 196 L396 196 C390 162, 350 162, 344 196 L168 196 C162 162, 122 162, 116 196 L62 196 C48 196, 45 185, 48 168 Z"
            fill="url(#mustang-body-gradient)"
          />

          {/* Aggressive Side Scoop & Character crease */}
          <path d="M140 134 Q 250 128, 380 132" stroke="#cbd5e1" strokeWidth="2.5" />
          <path d="M190 145 L220 172 L185 172 Z" fill="#e2e8f0" stroke="#cbd5e1" strokeWidth="1" />
          <path d="M260 118 L250 188" stroke="#cbd5e1" strokeWidth="1.5" />

          {/* Aggressive Shark-Nose Front Grille */}
          <path
            d="M430 135 C455 138, 470 148, 472 166 L424 186 Z"
            fill="#0f172a"
          />
          {/* Running Pony badge placeholder / honeycomb */}
          <rect x="444" y="148" width="14" height="8" rx="2" fill="#e2e8f0" />
          <line x1="432" y1="156" x2="466" y2="156" stroke="#334155" strokeWidth="2" />
          <line x1="428" y1="168" x2="458" y2="168" stroke="#334155" strokeWidth="2" />

          {/* Tri-Bar Signature LED DRL Headlights */}
          <path
            d="M416 126 L456 135 C460 142, 450 148, 436 145 L410 134 Z"
            fill="url(#mustang-light-gradient)"
          />
          <line x1="425" y1="130" x2="423" y2="140" stroke="#38bdf8" strokeWidth="2.5" />
          <line x1="433" y1="132" x2="431" y2="142" stroke="#38bdf8" strokeWidth="2.5" />
          <line x1="441" y1="134" x2="439" y2="144" stroke="#38bdf8" strokeWidth="2.5" />

          {/* Tri-Bar Rear Vertical Taillights */}
          <line x1="56" y1="140" x2="56" y2="155" stroke="#ef4444" strokeWidth="3" />
          <line x1="62" y1="140" x2="62" y2="155" stroke="#ef4444" strokeWidth="3" />
          <line x1="68" y1="140" x2="68" y2="155" stroke="#ef4444" strokeWidth="3" />

          {/* Black Machined Sport Wheels (GT Spec) */}
          <circle cx="370" cy="192" r="35" fill="#020617" />
          <circle cx="370" cy="192" r="29" fill="#1e293b" />
          <circle cx="370" cy="192" r="23" fill="#cbd5e1" />
          <circle cx="370" cy="192" r="8" fill="#090d14" />
          {/* Twin 5-spoke design */}
          <path d="M370 169 L370 215 M347 192 L393 192 M354 176 L386 208 M354 208 L386 176" stroke="#0f172a" strokeWidth="3.5" />
          <circle cx="370" cy="192" r="5" fill="#f8fafc" />

          <circle cx="142" cy="192" r="35" fill="#020617" />
          <circle cx="142" cy="192" r="29" fill="#1e293b" />
          <circle cx="142" cy="192" r="23" fill="#cbd5e1" />
          <circle cx="142" cy="192" r="8" fill="#090d14" />
          <path d="M142 169 L142 215 M119 192 L165 192 M126 176 L158 208 M126 208 L158 176" stroke="#0f172a" strokeWidth="3.5" />
          <circle cx="142" cy="192" r="5" fill="#f8fafc" />

          {/* Gradients */}
          <defs>
            <linearGradient id="mustang-body-gradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#ffffff" />
              <stop offset="60%" stopColor="#f1f5f9" />
              <stop offset="100%" stopColor="#cbd5e1" />
            </linearGradient>
            <linearGradient id="mustang-glass-gradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#64748b" stopOpacity="0.8" />
              <stop offset="100%" stopColor="#0f172a" stopOpacity="0.9" />
            </linearGradient>
            <linearGradient id="mustang-light-gradient" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#e2e8f0" />
              <stop offset="100%" stopColor="#e0f2fe" />
            </linearGradient>
          </defs>
        </svg>
      );

    case 'tesla_model3':
    case 'porsche_911':
    case 'audi_q7':
    case 'jeep_wrangler':
    case 'mercedes_cclass':
    case 'chrysler_pacifica':
    default:
      // Generic Premium Sleek Car Illustration
      return (
        <svg
          viewBox="0 0 500 240"
          className={className}
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <ellipse cx="250" cy="216" rx="210" ry="14" fill="#000000" fillOpacity="0.16" filter="blur(6px)" />
          <path
            d="M50 170 C55 138, 75 128, 110 122 L160 110 L220 68 C245 54, 312 54, 354 72 L418 116 C450 120, 468 142, 466 170 C464 190, 446 195, 428 195 L396 195 C390 163, 352 163, 346 195 L168 195 C162 163, 124 163, 118 195 L65 195 C50 195, 48 185, 50 170 Z"
            fill="url(#premium-generic-body)"
          />
          <path d="M174 110 L226 70 C245 58, 308 58, 350 74 L408 112 Z" fill="#0f172a" />
          <path d="M182 108 L230 73 C242 62, 290 62, 292 62 L292 108 Z" fill="#334155" />
          <path d="M300 62 C305 62, 344 68, 352 76 L400 108 L300 108 Z" fill="#1e293b" />
          <circle cx="372" cy="192" r="34" fill="#0f172a" />
          <circle cx="372" cy="192" r="23" fill="#94a3b8" />
          <circle cx="372" cy="192" r="8" fill="#0f172a" />
          <circle cx="144" cy="192" r="34" fill="#0f172a" />
          <circle cx="144" cy="192" r="23" fill="#94a3b8" />
          <circle cx="144" cy="192" r="8" fill="#0f172a" />
          <defs>
            <linearGradient id="premium-generic-body" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#475569" />
              <stop offset="50%" stopColor="#334155" />
              <stop offset="100%" stopColor="#0f172a" />
            </linearGradient>
          </defs>
        </svg>
      );
  }
}
